import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home, Fingerprint, Briefcase, Send, MessageSquare, Bell,
  Users as UsersIcon, ShieldCheck, LogOut, Search, Building2, PlusCircle, Sparkles,
  Wallet as WalletIcon, Compass, GraduationCap, Rocket, LineChart, Award, BookOpen, FolderOpen,
  Menu, X, LayoutGrid, Globe, Shield, FlaskConical, HandHeart,
} from "lucide-react";
import { useAuth, ROLE_LABELS } from "../context/AuthContext";
import { useAppNavigation } from "../hooks/useAppNavigation";
import { useEffect, useState, useRef } from "react";
import { api } from "../services/api";
import { toast } from "sonner";
import NotificationCenter from "./NotificationCenter";

/**
 * Shared authenticated layout: sidebar + topbar + main slot.
 * Used by every private page (Dashboard, Profile, Offers, Applications, Messages, ...).
 */

const NAV_COMMON = [
  { path: "/dashboard", label: "Dashboard", icon: Home, roles: ["*"] },
  { path: "/profile", label: "Mon profil", icon: Fingerprint, roles: ["*"] },
  { path: "/portfolio", label: "Portfolio", icon: BookOpen, roles: ["talent", "freelance", "entrepreneur", "coach"] },
  { path: "/offers", label: "Offres", icon: Briefcase, roles: ["*"] },
  { path: "/offers/new", label: "Publier une offre", icon: PlusCircle, roles: ["employeur", "admin", "super-admin"] },
  { path: "/matching", label: "Matching IA", icon: Sparkles, roles: ["talent", "freelance", "employeur"] },
  { path: "/talents", label: "Rechercher talents", icon: UsersIcon, roles: ["employeur", "entrepreneur", "admin", "super-admin"] },
  { path: "/applications", label: "Candidatures", icon: Send, roles: ["*"] },
  { path: "/missions", label: "Missions", icon: Compass, roles: ["*"] },
  { path: "/formations", label: "Formations", icon: GraduationCap, roles: ["talent", "freelance", "universite", "entrepreneur", "admin", "super-admin"] },
  { path: "/certifications", label: "Certifications", icon: Award, roles: ["talent", "freelance", "entrepreneur"] },
  { path: "/projects", label: "Projets & Deal flow", icon: Rocket, roles: ["entrepreneur", "investisseur", "admin", "super-admin"] },
  { path: "/invitations", label: "Mes invitations", icon: Send, roles: ["talent", "freelance", "coach", "universite"] },
  { path: "/company", label: "Mon entreprise", icon: Building2, roles: ["employeur", "admin", "super-admin"] },
  { path: "/wallet", label: "Wallet", icon: WalletIcon, roles: ["*"] },
  { path: "/escrow", label: "Séquestres", icon: Shield, roles: ["employeur", "entrepreneur", "talent", "freelance", "coach", "admin", "super-admin"] },
  { path: "/lab", label: "Le Lab", icon: FlaskConical, roles: ["talent", "freelance", "employeur", "entrepreneur", "admin", "super-admin"] },
  { path: "/coach", label: "Coach IA", icon: Sparkles, roles: ["*"] },
  { path: "/orchestrator", label: "Orchestrateur IA", icon: Rocket, roles: ["*"] },
  { path: "/messages", label: "Messagerie", icon: MessageSquare, roles: ["*"] },
  { path: "/workspaces", label: "Workspaces (projets)", icon: FolderOpen, roles: ["*"] },
  { path: "/notifications", label: "Notifications", icon: Bell, roles: ["*"] },
  { path: "/subscriptions", label: "Offres & Abonnements", icon: Award, roles: ["*"] },
];

const NAV_ADMIN = [
  { path: "/admin/ecosysteme", label: "Écosystème AFRICTAL", icon: Globe, roles: ["admin", "super-admin"] },
  { path: "/admin/jobs", label: "Référentiel Métiers", icon: LayoutGrid, roles: ["admin", "super-admin"] },
  { path: "/admin/competences", label: "Référentiel Compétences", icon: GraduationCap, roles: ["admin", "super-admin"] },
  { path: "/admin/freelance-profiles", label: "Profils Freelance (Gating)", icon: ShieldCheck, roles: ["admin", "super-admin"] },
  { path: "/admin/escrow", label: "Séquestres AFRICTAL", icon: Shield, roles: ["admin", "super-admin"] },
  { path: "/admin/command", label: "Centre de commandement", icon: ShieldCheck, roles: ["admin", "super-admin"] },
  { path: "/admin/accompagnements", label: "Accompagnements AFRICTAL", icon: Sparkles, roles: ["admin", "super-admin"] },
  { path: "/admin/moderation", label: "Modération comptes", icon: ShieldCheck, roles: ["admin", "super-admin"] },
  { path: "/admin/announcements", label: "Notifications & Annonces", icon: Send, roles: ["admin", "super-admin"] },
  { path: "/admin/site-settings", label: "CMS · Page d'accueil", icon: LayoutGrid, roles: ["admin", "super-admin"] },
  { path: "/admin/payments", label: "Configuration Paiements", icon: WalletIcon, roles: ["admin", "super-admin"] },
  { path: "/admin/users", label: "Gestion utilisateurs", icon: UsersIcon, roles: ["admin", "super-admin"] },
  { path: "/admin/offers", label: "Modération offres", icon: ShieldCheck, roles: ["admin", "super-admin"] },
  { path: "/admin/mission-matches", label: "Mises en relation missions", icon: ShieldCheck, roles: ["admin", "super-admin"] },
];

const NAV_SOON = [];

export default function PrivateLayout({ children, title, breadcrumbs = [], actions }) {
  const { user, logout } = useAuth();
  const { comingSoon } = useAppNavigation();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifCount, setNotifCount] = useState(0);
  const [accompCount, setAccompCount] = useState(0);
  const [systemAlerts, setSystemAlerts] = useState({ items: [], count: 0, has_critical: false, by_category: {} });
  const [mobileOpen, setMobileOpen] = useState(false);
  const prevAccompRef = useRef(null);

  // Close the mobile drawer when navigating to a new page
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    let mounted = true;
    api.get("/notifications").then(({ data }) => {
      if (mounted) setNotifCount(data.unread || 0);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [location.pathname]);

  // Real-time count of pending accompagnements (admin/super-admin only).
  // Polls every 25 s. If a new pending item appears, show a toast.
  useEffect(() => {
    const role = user?.role;
    if (role !== "admin" && role !== "super-admin") return undefined;
    let mounted = true;
    let intervalId = null;

    const fetchCount = async (isInitial) => {
      try {
        const { data } = await api.get("/africtal-orchestrator/pending");
        if (!mounted) return;
        const count = (data.items || []).length;
        setAccompCount(count);
        const prev = prevAccompRef.current;
        if (!isInitial && prev !== null && count > prev) {
          const delta = count - prev;
          toast.info(
            delta === 1
              ? "Nouvelle demande d'accompagnement à traiter"
              : `${delta} nouvelles demandes d'accompagnement à traiter`,
            {
              action: {
                label: "Ouvrir",
                onClick: () => navigate("/admin/accompagnements"),
              },
              duration: 8000,
            }
          );
        }
        prevAccompRef.current = count;
      } catch {
        // silent — admin will see the count on next poll
      }
    };

    fetchCount(true);
    intervalId = setInterval(() => fetchCount(false), 25000);
    return () => {
      mounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [user?.role, navigate]);

  // V3.9 · System alerts (AI model, payments, …) — polls every 45 s
  const prevAlertsRef = useRef(0);
  useEffect(() => {
    const role = user?.role;
    if (role !== "admin" && role !== "super-admin") return undefined;
    let mounted = true;
    let intervalId = null;

    const fetchAlerts = async (isInitial) => {
      try {
        const { data } = await api.get("/v1/admin/system-alerts");
        if (!mounted) return;
        setSystemAlerts(data || { items: [], count: 0 });
        const prev = prevAlertsRef.current;
        if (!isInitial && data.count > prev && data.has_critical) {
          const first = (data.items || [])[0];
          toast.error(first?.title || "Nouvelle alerte système", {
            description: first?.body?.slice(0, 140),
            action: first?.action ? {
              label: first.action.label || "Voir",
              onClick: () => navigate(first.action.href || "/admin"),
            } : undefined,
            duration: 10000,
          });
        }
        prevAlertsRef.current = data.count || 0;
      } catch {
        // silent
      }
    };

    fetchAlerts(true);
    intervalId = setInterval(() => fetchAlerts(false), 45000);
    return () => {
      mounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [user?.role, navigate]);

  const role = user?.role || "talent";
  const roleLabel = ROLE_LABELS[role] || "Utilisateur";

  // V3.20.7 — Client Mode pour Talents (entraide entre Bâtisseurs)
  const [showClientWelcome, setShowClientWelcome] = useState(false);
  const openClientMode = () => {
    const seen = (() => {
      try { return localStorage.getItem("africtal_client_mode_seen") === "1"; } catch { return false; }
    })();
    if (seen) {
      navigate("/offers/new");
    } else {
      setShowClientWelcome(true);
    }
  };
  const confirmClientMode = () => {
    try { localStorage.setItem("africtal_client_mode_seen", "1"); } catch {}
    setShowClientWelcome(false);
    navigate("/offers/new");
  };
  const nav = [
    ...NAV_COMMON.filter((n) => n.roles.includes("*") || n.roles.includes(role)),
    ...NAV_ADMIN.filter((n) => n.roles.includes(role)),
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="afr-root" data-testid="private-layout">
      <div className="afr-shell">
        {/* Mobile drawer backdrop */}
        {mobileOpen && (
          <button
            type="button"
            className="afr-sidebar__backdrop"
            aria-label="Fermer le menu"
            onClick={() => setMobileOpen(false)}
            data-testid="layout-sidebar-backdrop"
          />
        )}
        <aside className={`afr-sidebar${mobileOpen ? " afr-sidebar--open" : ""}`} data-testid="layout-sidebar">
          <Link to="/" className="afr-sidebar__brand" style={{ textDecoration: "none", color: "inherit" }} data-testid="layout-brand-home">
            <span className="afr-mark" aria-hidden="true">
              <img src="/brand/africtal-logo.png" alt="AFRICTAL" />
            </span>
            <span>AFRICTAL</span>
          </Link>

          <span className="afr-sidebar__group-label">Espace {roleLabel}</span>
          {nav.map((it) => {
            const active =
              (it.path === "/dashboard" && location.pathname === "/dashboard") ||
              (it.path !== "/dashboard" && location.pathname.startsWith(it.path));
            return (
              <Link
                key={`${it.path}-${it.label}`}
                to={it.path}
                className={`afr-sidebar__item${active ? " afr-sidebar__item--active" : ""}`}
                data-testid={`layout-nav-${it.path.replace(/\//g, "-")}`}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <it.icon size={16} /> {it.label}
                {it.path === "/notifications" && notifCount > 0 && (
                  <span className="afr-sidebar__badge" data-testid="layout-notif-badge">{notifCount}</span>
                )}
                {it.path === "/admin/accompagnements" && accompCount > 0 && (
                  <span
                    className="afr-sidebar__badge"
                    data-testid="layout-accomp-badge"
                    style={{ background: "var(--afr-warning)", color: "#0F2A42" }}
                    aria-label={`${accompCount} demande(s) en attente`}
                  >
                    {accompCount}
                  </span>
                )}
                {/* V3.9 · Badges d'alertes système (rouge = critique) */}
                {it.path === "/admin/command" && systemAlerts.count > 0 && (
                  <span
                    className="afr-sidebar__badge"
                    data-testid="layout-system-alert-badge"
                    style={{
                      background: systemAlerts.has_critical ? "#B91C1C" : "#B45309",
                      color: "#fff",
                      animation: systemAlerts.has_critical ? "afr-pulse 2s ease-in-out infinite" : "none",
                    }}
                    aria-label={`${systemAlerts.count} alerte(s) système`}
                    title={systemAlerts.items.map((a) => a.title).join(" · ")}
                  >
                    {systemAlerts.count}
                  </span>
                )}
                {it.path === "/admin/payments" && (systemAlerts.by_category?.payments || 0) > 0 && (
                  <span
                    className="afr-sidebar__badge"
                    data-testid="layout-payments-alert-badge"
                    style={{ background: "#B45309", color: "#fff" }}
                    aria-label={`${systemAlerts.by_category.payments} alerte(s) paiement`}
                    title="Configuration provider incomplète"
                  >
                    !
                  </span>
                )}
              </Link>
            );
          })}

          {NAV_SOON.length > 0 && (
            <>
              <span className="afr-sidebar__group-label">Disponible prochainement</span>
              {NAV_SOON.map((it) => (
                <button
                  type="button"
                  key={it.label}
                  className="afr-sidebar__item"
                  onClick={() => comingSoon(it.label)}
                  data-testid={`layout-soon-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                  style={{ width: "100%", border: "none", background: "transparent", font: "inherit", color: "inherit", textAlign: "left", opacity: 0.75 }}
                >
                  <it.icon size={16} /> {it.label}
                  <span className="afr-sidebar__badge" style={{ background: "var(--afr-warning)" }}>Bientôt</span>
                </button>
              ))}
            </>
          )}

          {/* V3.20.7 — Client Mode : entraide entre Bâtisseurs (Talents uniquement) */}
          {role === "talent" && (
            <button
              type="button"
              onClick={openClientMode}
              className="afr-sidebar__item"
              style={{
                width: "100%", border: "1px dashed rgba(15,42,66,0.18)",
                background: "transparent", font: "inherit", color: "rgba(15,42,66,0.72)",
                textAlign: "left", marginTop: "0.75rem", borderRadius: 10,
              }}
              data-testid="layout-btn-client-mode"
              title="Publier un besoin ou une mission pour un autre Bâtisseur"
            >
              <HandHeart size={16} /> Besoin d'aide ? Passer en mode Client
            </button>
          )}

          <button
            type="button"
            onClick={handleLogout}
            className="afr-sidebar__item"
            style={{ width: "100%", border: "none", background: "transparent", font: "inherit", color: "inherit", textAlign: "left", marginTop: "1rem" }}
            data-testid="layout-btn-logout"
          >
            <LogOut size={16} /> Déconnexion
          </button>
        </aside>

        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <div className="afr-topbar">
            <div className="afr-topbar__left">
              <button
                type="button"
                className="afr-topbar__burger"
                aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu"}
                aria-expanded={mobileOpen}
                onClick={() => setMobileOpen((v) => !v)}
                data-testid="layout-burger"
              >
                {mobileOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
              <span className="afr-topbar__title">{title || "AFRICTAL"}</span>
              <span className={`afr-badge afr-badge--${roleBadge(role)}`} data-testid="layout-user-badge">
                {user?.name || roleLabel}
              </span>
            </div>
            <div className="afr-topbar__right">
              <label className="afr-search">
                <Search size={16} />
                <input
                  placeholder="Rechercher une offre…"
                  data-testid="layout-search"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const v = e.currentTarget.value.trim();
                      navigate(`/offers${v ? `?q=${encodeURIComponent(v)}` : ""}`);
                    }
                  }}
                />
              </label>
              <Link to="/messages" className="afr-topbar__icon" aria-label="Messagerie" data-testid="topbar-messages">
                <MessageSquare size={16} />
              </Link>
              <NotificationCenter />
              <Link to="/profile" className="afr-topbar__icon" aria-label="Profil" data-testid="topbar-profile">
                <span style={{ fontSize: "0.75rem", fontWeight: 700 }}>{(user?.name || "?").charAt(0).toUpperCase()}</span>
              </Link>
            </div>
          </div>

          <div className="afr-shell__main">
            <nav className="afr-breadcrumb" aria-label="Fil d'ariane" data-testid="layout-breadcrumb">
              <Link to="/" data-testid="crumb-home">Accueil</Link>
              <span className="afr-breadcrumb__sep">/</span>
              <Link to="/dashboard" data-testid="crumb-dashboard">Dashboard {roleLabel}</Link>
              {breadcrumbs.map((c, i) => (
                <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
                  <span className="afr-breadcrumb__sep">/</span>
                  {c.to ? <Link to={c.to}>{c.label}</Link> : <span className="afr-breadcrumb__current">{c.label}</span>}
                </span>
              ))}
            </nav>

            {(title || actions) && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem", marginBottom: "1.4rem" }}>
                <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: "1.8rem", margin: 0, color: "var(--afr-secondary)" }}>{title}</h1>
                {actions && <div className="afr-flex-row">{actions}</div>}
              </div>
            )}

            {children}
          </div>
        </div>
      </div>

      {/* V3.20.7 — Modale bienvenue Client Mode (premier clic Talent) */}
      {showClientWelcome && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="client-mode-title"
          data-testid="client-mode-welcome"
          style={{
            position: "fixed", inset: 0, zIndex: 10000,
            background: "rgba(15,42,66,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 16,
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowClientWelcome(false); }}
        >
          <div style={{
            background: "#fff", borderRadius: 16, maxWidth: 480, width: "100%",
            padding: "1.75rem", boxShadow: "0 20px 60px rgba(15,42,66,0.25)",
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <span style={{
                width: 38, height: 38, borderRadius: 10, background: "#E8F5EE",
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}>
                <HandHeart size={20} color="#009640" />
              </span>
              <h2 id="client-mode-title" style={{
                margin: 0, fontFamily: "'Outfit', sans-serif", fontSize: "1.25rem",
                color: "var(--afr-secondary)",
              }}>Ici, les Bâtisseurs s'entraident</h2>
            </div>
            <p style={{ margin: "0 0 0.85rem", color: "rgba(15,42,66,0.75)", fontSize: "0.9rem", lineHeight: 1.55 }}>
              Vous passez temporairement en <b>mode Client</b> pour publier un besoin
              ou une mission destinée à un autre Talent AFRICTAL. C'est ce qui permet
              à notre écosystème de tourner : les Bâtisseurs s'aident entre eux.
            </p>
            <div style={{
              background: "#FFF7E6", border: "1px solid #F4D08C",
              borderRadius: 10, padding: "0.75rem 0.85rem",
              color: "#7A4E00", fontSize: "0.82rem", lineHeight: 1.5, marginBottom: "1rem",
            }} data-testid="client-mode-security-notice">
              <b>⚠ Sécurité :</b> vous pouvez publier votre besoin <b>quel que soit
              votre solde</b>. La mission ne <b>démarrera qu'après validation Admin
              du dépôt</b> (Séquestre), pour garantir le paiement au Talent qui vous aidera.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => setShowClientWelcome(false)}
                className="afr-btn afr-btn--outline afr-btn--sm"
                data-testid="client-mode-cancel"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={confirmClientMode}
                className="afr-btn afr-btn--sm"
                style={{ background: "#009640", color: "#fff", border: "none" }}
                data-testid="client-mode-confirm"
              >
                Publier un besoin
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function roleBadge(role) {
  switch (role) {
    case "talent": return "talent";
    case "employeur": return "emploi";
    case "freelance": return "freelance";
    case "universite": return "formation";
    case "entrepreneur": return "entrepreneur";
    case "investisseur": return "investissement";
    case "coach": return "coach-ai";
    default: return "admin";
  }
}
